import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router'
import { SettingsLayout } from '@/components/SettingsLayout/SettingsLayout'
import { AuthLayout } from '@/core/AuthLayout'
import { SecurityGroupGuardedRoute } from '@/components/SecurityGroupGuardedRoute'
import { SettingsIndexRedirect } from '@/components/SettingsIndexRedirect'
import { AdminIndexRedirect } from '@/components/AdminIndexRedirect'
import {
    SECURITY_GROUP_ADMIN,
    SECURITY_GROUP_QA_TESTING,
    SECURITY_GROUP_ONESPAN_ESIGN_LAYOUTS,
    SECURITY_GROUP_PAYROLL_MANAGER,
} from '@/constants/securityGroups'
import {
    RouteErrorBoundaryFallback,
    LoadingSpinner,
    FeatureGuardedRoute,
    ModuleLayout,
    AccessDeniedPage,
} from '@m3-tech/boss-ui'
import { PrivacyPolicyGuardedRoute } from '@/components/PrivacyPolicyGuardedRoute'
import { TemplateGuardedRoute } from '@/components/TemplateGuardedRoute'
import { DevServiceStatusGuardedRoute } from '@/components/DevServiceStatusGuardedRoute'
import {
    FEATURE_AUTO_COMMUNICATION,
    FEATURE_BROKER_RATES,
    FEATURE_COMMISSION_REPORTS_PASSWORD,
    FEATURE_CTP_PLATFORM_DEFAULTS,
    FEATURE_CTP_PLATFORM_DOCUMENTS,
    FEATURE_CTP_PLATFORM_EMAILS,
    FEATURE_CTP_PLATFORM_NOTIFICATIONS,
    FEATURE_CUSTOM_SIGNATURE,
    FEATURE_CUSTOMER_PORTAL_SETTINGS_SUBITEM,
    FEATURE_GROSS_COMMISSION_VISIBILITY,
    FEATURE_INTEGRATION_SETTINGS,
    FEATURE_MAILCHIMP,
    FEATURE_MARKETING_INFO,
    FEATURE_MOPOLO,
    FEATURE_NEW_PRESET_COMMISSION,
    FEATURE_PERSONAL_INFO,
    FEATURE_PROVINCIAL_TAX_RATE,
    FEATURE_SECURITY_SETTINGS,
    FEATURE_SYSTEM_SETTINGS,
    FEATURE_USER_SETTINGS,
    FEATURE_WEBSITE,
} from '@/constants/featureIds'
import { SECURITY_GROUP_FEATURE_ADMIN } from '@/constants/securityGroups'

const PlaceholderPage = lazy(() => import('@/pages/PlaceholderPage/PlaceholderPage'))
const SecuritySettings = lazy(() => import('@/pages/SecuritySettings'))
const TemplateList = lazy(() => import('@/pages/TemplateList'))
const TemplateNew = lazy(() => import('@/pages/TemplateNew'))
const TemplateDetail = lazy(() => import('@/pages/TemplateDetail'))
const TaskTemplateList = lazy(() => import('@/pages/TaskTemplateList'))
const TaskTemplateNew = lazy(() => import('@/pages/TaskTemplateNew'))
const TaskTemplateDetail = lazy(() => import('@/pages/TaskTemplateDetail'))
const SystemSettingsPage = lazy(() => import('@/pages/SystemSettings'))
const IntegrationSettingsList = lazy(() => import('@/pages/IntegrationSettingsList'))
const IntegrationSettingsDetail = lazy(() => import('@/pages/IntegrationSettingsDetail'))
const PersonalInformationPage = lazy(() => import('@/pages/PersonalInformation'))
const CustomerPortalSettingsPage = lazy(() => import('@/pages/CustomerPortalSettings'))
const UserSettingsPage = lazy(() => import('@/pages/UserSettings'))
const ProvincialTaxRatePage = lazy(() => import('@/pages/ProvincialTaxRate'))
const CommissionReportsPasswordPage = lazy(() => import('@/pages/CommissionReportsPassword'))
const FeatureManagementPage = lazy(() => import('@/pages/FeatureManagement'))
const CreateFeatureProfilePage = lazy(() => import('@/pages/CreateFeatureProfile'))
const UpdateFeatureProfilePage = lazy(() => import('@/pages/UpdateFeatureProfile'))
const ConfiguredProfilesPage = lazy(() => import('@/pages/ConfiguredProfiles'))
const MarketingInformationPage = lazy(() => import('@/pages/MarketingInformation'))
const GrossCommissionPage = lazy(() => import('@/pages/GrossCommission'))
const MopoloSettingsPage = lazy(() => import('@/pages/MopoloSettings'))
const WebsiteSettingsPage = lazy(() => import('@/pages/WebsiteSettings'))
const MailchimpPage = lazy(() => import('@/pages/Mailchimp'))
const CtpPlatformDefaultsPage = lazy(() => import('@/pages/CtpPlatformDefaults'))
const CtpPlatformDocumentsPage = lazy(() => import('@/pages/CtpPlatformDocuments'))
const CtpPlatformEmailsPage = lazy(() => import('@/pages/CtpPlatformEmails'))
const CtpPlatformNotificationsPage = lazy(() => import('@/pages/CtpPlatformNotifications'))
const BrokerRatesPage = lazy(() => import('@/pages/BrokerRates'))
const BrandRatesPage = lazy(() => import('@/pages/BrandRates'))
const NewPresetCommissionPage = lazy(() => import('@/pages/NewPresetCommission'))
const AdminPresetCommissionPage = lazy(() => import('@/pages/AdminPresetCommission'))
const CustomSignaturePage = lazy(() => import('@/pages/CustomSignature'))
const BrokerPlatformAnnouncementsPage = lazy(() => import('@/pages/BrokerPlatformAnnouncements'))
const BrokerPlatformBlogsPage = lazy(() => import('@/pages/BrokerPlatformBlogs'))
const BrokerPlatformMortgageServicesPage = lazy(
    () => import('@/pages/BrokerPlatformMortgageServices'),
)
const BrokerPlatformTestimonialsPage = lazy(() => import('@/pages/BrokerPlatformTestimonials'))
const BrokerPlatformPrivacyPoliciesPage = lazy(
    () => import('@/pages/BrokerPlatformPrivacyPolicies'),
)
const CorporateContentPage = lazy(() => import('@/pages/CorporateContent'))
const LandTransferTaxPage = lazy(() => import('@/pages/LandTransferTax'))
const AutoCommunicationPage = lazy(() => import('@/pages/AutoCommunication'))
const DataTransformationPage = lazy(() => import('@/pages/DataTransformation'))
const DataImportSummaryPage = lazy(() => import('@/pages/DataImportSummary'))
const DataImportSummaryDetailPage = lazy(() => import('@/pages/DataImportSummaryDetail'))
const AdminWebsiteSettingsPage = lazy(() => import('@/pages/AdminWebsiteSettings'))
const AdminProgramsPage = lazy(() => import('@/pages/AdminPrograms'))
const AdminCommissionVisibilityPage = lazy(() => import('@/pages/AdminCommissionVisibility'))
const AdminSignalRPage = lazy(() => import('@/pages/AdminSignalR'))
const QATestingPage = lazy(() => import('@/pages/QATesting'))
const ErrorManagementPage = lazy(() => import('@/pages/ErrorManagement'))
const TransactionsToReviewPage = lazy(() => import('@/pages/TransactionsToReview'))
const TransactionToReviewDetailPage = lazy(() => import('@/pages/TransactionToReviewDetail'))
const DevServiceStatusPage = lazy(() => import('@/pages/DevServiceStatus'))
const NotFound = lazy(() => import('@/pages/NotFound'))

const settingsRoutes = ['onespan/layouts', 'onespan/layouts/new'] as const

export const router = createBrowserRouter([
    {
        element: <AuthLayout />,
        errorElement: <RouteErrorBoundaryFallback />,
        children: [
            {
                path: '/',
                element: (
                    <ModuleLayout
                        moduleKey={import.meta.env.VITE_MODULE_NAME?.toLowerCase() ?? 'settings'}
                        testIdBase="setting"
                        fullHeight
                    />
                ),
                children: [
                    {
                        element: <SettingsLayout />,
                        children: [
                            {
                                index: true,
                                element: <SettingsIndexRedirect />,
                            },
                            {
                                path: 'system-settings',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_SYSTEM_SETTINGS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <SystemSettingsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'pin-code',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_SECURITY_SETTINGS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <SecuritySettings />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'personal-information',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_PERSONAL_INFO}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <PersonalInformationPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'customer-portal',
                                element: (
                                    <FeatureGuardedRoute
                                        featureId={FEATURE_CUSTOMER_PORTAL_SETTINGS_SUBITEM}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CustomerPortalSettingsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'user-settings',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_USER_SETTINGS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <UserSettingsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'provincial-tax-rate',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_PROVINCIAL_TAX_RATE}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <ProvincialTaxRatePage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'commission-reports-password',
                                element: (
                                    <FeatureGuardedRoute
                                        featureId={FEATURE_COMMISSION_REPORTS_PASSWORD}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CommissionReportsPasswordPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'marketing-information',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_MARKETING_INFO}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <MarketingInformationPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'gross-commission',
                                element: (
                                    <FeatureGuardedRoute
                                        featureId={FEATURE_GROSS_COMMISSION_VISIBILITY}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <GrossCommissionPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'mopolo',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_MOPOLO}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <MopoloSettingsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'website',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_WEBSITE}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <WebsiteSettingsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'mailchimp',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_MAILCHIMP}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <MailchimpPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'ctp-platform-defaults',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_CTP_PLATFORM_DEFAULTS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CtpPlatformDefaultsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'ctp-platform-documents',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_CTP_PLATFORM_DOCUMENTS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CtpPlatformDocumentsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'ctp-platform-emails',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_CTP_PLATFORM_EMAILS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CtpPlatformEmailsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'ctp-platform-notifications',
                                element: (
                                    <FeatureGuardedRoute
                                        featureId={FEATURE_CTP_PLATFORM_NOTIFICATIONS}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CtpPlatformNotificationsPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'broker-rates',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_BROKER_RATES}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <BrokerRatesPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/brand-rates',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrandRatesPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'new-preset-commission',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_NEW_PRESET_COMMISSION}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <NewPresetCommissionPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'custom-signature',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_CUSTOM_SIGNATURE}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CustomSignaturePage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'auto-communication',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_AUTO_COMMUNICATION}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AutoCommunicationPage />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/announcements',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrokerPlatformAnnouncementsPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'admin/preset-commission',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AdminPresetCommissionPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/data-transformation',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <DataTransformationPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/data-import-summary',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <DataImportSummaryPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                // Reached from the list's Details action, not from the
                                // nav. Legacy passed the id as `?id=`; the modern route
                                // takes it as a path param.
                                path: 'admin/data-import-summary/:id',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <DataImportSummaryDetailPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/signalr',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AdminSignalRPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/error-management',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <ErrorManagementPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            // FF-2856 / FF-2857 — Business Central "Transactions to review".
                            // Payroll Manager (96001) only; the API refuses everyone else.
                            // Access Denied is the card variant so the menu stays visible
                            // (screen 3).
                            {
                                path: 'admin/transactions-to-review',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_PAYROLL_MANAGER}
                                        fallback={<AccessDeniedPage variant="card" />}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <TransactionsToReviewPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                // The rejection email (FF-2855) links here.
                                path: 'admin/transactions-to-review/:id',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_PAYROLL_MANAGER}
                                        fallback={<AccessDeniedPage variant="card" />}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <TransactionToReviewDetailPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                // Root-level, not under /admin — legacy `unshift`es this
                                // above Admin in the sidebar and it has no security group.
                                path: 'dev-service-status',
                                element: (
                                    <DevServiceStatusGuardedRoute>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <DevServiceStatusPage />
                                        </Suspense>
                                    </DevServiceStatusGuardedRoute>
                                ),
                            },
                            {
                                path: 'qa-testing',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_QA_TESTING}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <QATestingPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/website-settings',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AdminWebsiteSettingsPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/programs',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AdminProgramsPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/commission-visibility',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_FEATURE_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AdminCommissionVisibilityPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'broker-platform-blogs',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrokerPlatformBlogsPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'admin/blogs',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrokerPlatformBlogsPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'broker-platform-testimonials',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrokerPlatformTestimonialsPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'admin/testimonials',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrokerPlatformTestimonialsPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'broker-platform-mortgage-services',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrokerPlatformMortgageServicesPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'admin/mortgage-services',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <BrokerPlatformMortgageServicesPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'broker-platform-privacy-policy',
                                element: (
                                    <PrivacyPolicyGuardedRoute>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <BrokerPlatformPrivacyPoliciesPage />
                                        </Suspense>
                                    </PrivacyPolicyGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/privacy-policy',
                                element: (
                                    <PrivacyPolicyGuardedRoute>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <BrokerPlatformPrivacyPoliciesPage />
                                        </Suspense>
                                    </PrivacyPolicyGuardedRoute>
                                ),
                            },
                            {
                                path: 'admin/corporate-content',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <CorporateContentPage />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'admin/land-transfer-tax',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <LandTransferTaxPage />
                                    </Suspense>
                                ),
                            },
                            ...settingsRoutes.map((path) => ({
                                path,
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_ONESPAN_ESIGN_LAYOUTS}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <PlaceholderPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            })),
                            {
                                path: 'templates',
                                element: (
                                    <TemplateGuardedRoute>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <TemplateList />
                                        </Suspense>
                                    </TemplateGuardedRoute>
                                ),
                            },
                            {
                                path: 'templates/new',
                                element: (
                                    <TemplateGuardedRoute>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <TemplateNew />
                                        </Suspense>
                                    </TemplateGuardedRoute>
                                ),
                            },
                            {
                                path: 'templates/:objectId',
                                element: (
                                    <TemplateGuardedRoute>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <TemplateDetail />
                                        </Suspense>
                                    </TemplateGuardedRoute>
                                ),
                            },
                            {
                                path: 'task-templates',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <TaskTemplateList />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'task-templates/new',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <TaskTemplateNew />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'task-templates/:objectId',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <TaskTemplateDetail />
                                    </Suspense>
                                ),
                            },
                            {
                                path: 'integrations',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_INTEGRATION_SETTINGS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <IntegrationSettingsList />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'integrations/:apiPartnersId',
                                element: (
                                    <FeatureGuardedRoute featureId={FEATURE_INTEGRATION_SETTINGS}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <IntegrationSettingsDetail />
                                        </Suspense>
                                    </FeatureGuardedRoute>
                                ),
                            },
                            {
                                path: 'feature-management',
                                element: <Navigate to="/feature-management/general" replace />,
                            },
                            {
                                path: 'feature-management/general',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_FEATURE_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <FeatureManagementPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'feature-management/create-profile',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_FEATURE_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <CreateFeatureProfilePage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'feature-management/update-profile',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_FEATURE_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <UpdateFeatureProfilePage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                path: 'feature-management/configured-profiles',
                                element: (
                                    <SecurityGroupGuardedRoute
                                        securityGroupId={SECURITY_GROUP_FEATURE_ADMIN}
                                    >
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <ConfiguredProfilesPage />
                                        </Suspense>
                                    </SecurityGroupGuardedRoute>
                                ),
                            },
                            {
                                // C261373 — bare /admin redirects to the user's first accessible Admin
                                // section (legacy parity), resolved dynamically from the nav tree.
                                path: 'admin',
                                element: <AdminIndexRedirect />,
                            },
                            {
                                path: '*',
                                element: (
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <NotFound />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                ],
            },
        ],
    },
])
